import { useState, useEffect, useMemo } from 'react';
import { getUserTimezone } from '../utils/timezone';
import companyService from '../services/company';

const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/**
 * Custom hook to manage member and company timezones
 * @param {Object} options - Configuration options
 * @param {string} options.teamId - Team ID for fetching member timezone
 * @param {string} options.memberId - Member/User ID for fetching member timezone
 * @param {string} options.companyId - Company ID for fetching company timezone
 * @param {string} options.initialTimezone - Initial selected timezone (optional)
 * @param {boolean} options.autoSelectMemberTimezone - Automatically select member timezone when it loads (default: true)
 * @param {React.MutableRefObject<boolean>} [options.userOverrideTimezoneRef] - When `current` is true, member/company
 *   auto-select effects do not overwrite `selectedTimezone` (handles async fetch finishing after the user picked a TZ).
 *   Clear the ref when the member changes so the new member’s timezone can apply.
 * @returns {Object} - { timezoneOptions, selectedTimezone, setSelectedTimezone, timezonesLoading, memberTimezone, companyTimezone }
 */
export const useMemberCompanyTimezones = ({
  teamId,
  memberId,
  companyId,
  initialTimezone = null,
  autoSelectMemberTimezone = true,
  userOverrideTimezoneRef = null,
}) => {
  const [memberTimezone, setMemberTimezone] = useState(null);
  const [companyTimezone, setCompanyTimezone] = useState(null);
  const [timezonesLoading, setTimezonesLoading] = useState(true);
  const [selectedTimezone, setSelectedTimezone] = useState(
    initialTimezone || DEFAULT_TIMEZONE
  );

  // Fetch member timezone
  const fetchMemberTimezone = async (teamIdParam, userIdParam) => {
    if (!teamIdParam || !userIdParam) {
      setMemberTimezone(null);
      return;
    }
    try {
      const timezoneInfo = await getUserTimezone(teamIdParam, userIdParam);
      const tz = timezoneInfo.timezone;
      setMemberTimezone(tz);
    } catch (error) {
      console.error('Error fetching member timezone:', error);
      setMemberTimezone(null);
    }
  };

  // Fetch company timezone
  const fetchCompanyTimezone = async (companyIdParam) => {
    if (!companyIdParam) {
      setCompanyTimezone(null);
      return;
    }
    try {
      const response = await companyService.getCompanyTimezone(companyIdParam);
      if (response.success && response.timezone) {
        setCompanyTimezone(response.timezone);
      } else {
        setCompanyTimezone(null);
      }
    } catch (error) {
      console.error('Error fetching company timezone:', error);
      setCompanyTimezone(null);
    }
  };

  // Build timezone options from member and company timezones
  const timezoneOptions = useMemo(() => {
    const timezones = [];
    if (memberTimezone) {
      timezones.push({ value: memberTimezone, label: memberTimezone });
    }
    if (companyTimezone) {
      timezones.push({ value: companyTimezone, label: companyTimezone });
    }
    // Remove duplicates if both are the same
    const unique = Array.from(
      new Map(timezones.map((tz) => [tz.value, tz])).values()
    );
    return unique;
  }, [memberTimezone, companyTimezone]);

  // Fetch both timezones when dependencies change
  useEffect(() => {
    let isMounted = true;

    const loadTimezones = async () => {
      setTimezonesLoading(true);

      // Load both timezones in parallel
      const promises = [];

      if (companyId) {
        promises.push(fetchCompanyTimezone(companyId));
      } else {
        setCompanyTimezone(null);
      }

      if (teamId && memberId) {
        promises.push(fetchMemberTimezone(teamId, memberId));
      } else {
        setMemberTimezone(null);
      }

      // Wait for all promises to complete
      await Promise.all(promises);

      if (isMounted) {
        setTimezonesLoading(false);
      }
    };

    loadTimezones();

    return () => {
      isMounted = false;
    };
  }, [companyId, teamId, memberId]);

  // Sync controlled initial timezone (e.g. from URL on load or browser navigation)
  useEffect(() => {
    if (!initialTimezone) return;
    setSelectedTimezone((prev) =>
      prev === initialTimezone ? prev : initialTimezone
    );
  }, [initialTimezone]);

  // Auto-update selected timezone when member timezone changes (if auto-select is enabled)
  useEffect(() => {
    if (!autoSelectMemberTimezone) return;
    // Do not override a caller-provided initial timezone (e.g. from URL) on first member load
    if (initialTimezone) return;
    if (userOverrideTimezoneRef?.current) return;

    if (memberTimezone) {
      // Always update to member timezone when it's available (member changed)
      setSelectedTimezone(memberTimezone);
    }
  }, [
    memberTimezone,
    autoSelectMemberTimezone,
    initialTimezone,
    userOverrideTimezoneRef,
  ]);

  // Fallback to company timezone on initial load if no member timezone
  useEffect(() => {
    if (!autoSelectMemberTimezone || memberTimezone || initialTimezone) return;
    if (userOverrideTimezoneRef?.current) return;

    if (companyTimezone) {
      setSelectedTimezone(companyTimezone);
    }
  }, [
    companyTimezone,
    memberTimezone,
    autoSelectMemberTimezone,
    initialTimezone,
    userOverrideTimezoneRef,
  ]);

  // Ensure required flows always have a usable timezone.
  useEffect(() => {
    if (selectedTimezone) return;
    setSelectedTimezone(memberTimezone || companyTimezone || DEFAULT_TIMEZONE);
  }, [selectedTimezone, memberTimezone, companyTimezone]);

  return {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
    memberTimezone,
    companyTimezone,
  };
};
